import React, { useState, useEffect } from "react";
import moment from "moment";
import "moment/locale/es";
import {
  getFirestore,
  doc,
  updateDoc,
  collection,
  onSnapshot,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { useAuth } from "../context/AuthContext";
import Avatar from "./Avatar";

// Importa el Skeleton de una sola fila
import PedidosSkeleton from "./PedidosSkeleton";

Modal.setAppElement("#root");
moment.locale("es");

const PedidosActivos = () => {
  const [ordenesDeCompra, setOrdenesDeCompra] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [comentarios, setComentarios] = useState([]);
  const [usuario, setUsuario] = useState(null);
  const [modalData, setModalData] = useState({
    isOpen: false,
    orden: null,
  });

  const { user, avatarUrl, firstName, lastName } = useAuth();
  const navigate = useNavigate();
  const firestore = getFirestore();

  useEffect(() => {
    const ordenesRef = collection(firestore, "pedidos");
    const unsubscribe = onSnapshot(ordenesRef, (snapshot) => {
      const ordenes = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOrdenesDeCompra(ordenes);
      setIsLoading(false); // Listo, ya llegaron datos
    });
    return () => unsubscribe();
  }, [firestore]);

  useEffect(() => {
    if (user && firstName && lastName) {
      setUsuario({
        id: user.uid,
        nombre: firstName,
        apellido: lastName,
        avatarUrl: avatarUrl,
      });
    } else {
      setUsuario(null);
    }
  }, [user, firstName, lastName, avatarUrl]);

  const handleNuevoProyecto = () => {
    navigate("/orden-de-compra", { state: { openModal: true } });
  };

  const closeModal = () => {
    setModalData({ isOpen: false, orden: null });
  };

  const agregarComentario = async () => {
    if (nuevoComentario.trim() && modalData.orden) {
      const nuevoComentarioObj = {
        texto: nuevoComentario,
        fecha: moment().format("ddd DD/MM/YY hh:mm a"),
        usuario: {
          id: user.uid,
          nombre: firstName,
          apellido: lastName,
          avatarUrl: avatarUrl || "/img/avatar2.jpg",
        },
      };

      try {
        const ordenRef = doc(firestore, "pedidos", modalData.orden.id);
        const nuevosComentarios = [...comentarios, nuevoComentarioObj];
        await updateDoc(ordenRef, { comentarios: nuevosComentarios });

        setComentarios(nuevosComentarios);
        setOrdenesDeCompra((prev) =>
          prev.map((orden) =>
            orden.id === modalData.orden.id
              ? { ...orden, comentarios: nuevosComentarios }
              : orden
          )
        );
        setNuevoComentario("");
      } catch (error) {
        console.error("Error al agregar comentario:", error);
        Swal.fire("Error", "No se pudo agregar el comentario.", "error");
      }
    }
  };

  const calculateDaysLeft = (fechaFinRango) => {
    const today = moment().startOf("day");
    const endDate = moment(fechaFinRango);
    const difference = endDate.diff(today, "days");

    if (difference > 0) {
      return `Quedan ${difference} días`;
    } else if (difference === 0) {
      return "Vence hoy";
    } else {
      return "Vencido";
    }
  };

  const openModal = (orden) => {
    setComentarios(orden.comentarios || []);
    setModalData({ isOpen: true, orden });
  };

  return (
    <div style={{ paddingLeft: "70px" }}>
      <div className="proyectos-section">
        <h3 className="proyectos-title">Pedidos Activos</h3>
        <div className="proyectos-header">
          <div className="header-actions-proyectos">
            <button className="action-button-proyectos">
              <i className="bi bi-funnel-fill"></i> Todos
              <i
                className="bi bi-chevron-down"
                style={{ fontSize: "12px" }}
              ></i>
            </button>
            <div className="buscador-pedidos">
              <i className="bi bi-search"></i>
              <input
                type="text"
                className="buscador-input"
                placeholder="Buscar"
              />
            </div>
          </div>

          <div>
            <button className="exportar-button">
              <i
                className="bi bi-cloud-arrow-down"
                style={{ marginRight: "5px" }}
              ></i>
              Exportar
            </button>

            <button
              onClick={handleNuevoProyecto}
              className="new-proyecto-button"
            >
              + Agregar Pedido
            </button>
          </div>
        </div>

        {/* AQUI RENDERIZAMOS LA TABLA SIEMPRE */}
        <div className="proyecto-list-container">
          <table className="proyecto-tabla">
            {!isLoading && (
              <thead>
                <tr>
                  <th>Descripción</th>
                  <th>Usuarios</th>
                  <th>Tiempo Restante</th>
                  <th>Comentarios</th>
                  <th>Estado</th>
                </tr>
              </thead>
            )}

            <tbody>
              {/*
                Si está cargando => solo muestra UNA FILA con el Skeleton
              */}
              {isLoading && (
                <tr>
                  <td colSpan={5}>
                    <PedidosSkeleton />
                  </td>
                </tr>
              )}

              {/*
                Si YA NO está cargando => Mapea los pedidos REALES
              */}
              {!isLoading &&
                ordenesDeCompra.map((orden) => {
                  const assignedUsers = orden.usuarios || [];
                  // eliminamos duplicados si fuera necesario
                  const uniqueAssigned = Array.from(
                    new Map(assignedUsers.map((id) => [id, id])).values()
                  );
                  // UID de quien creó el pedido
                  const creatorId = orden.createdBy || orden.ownerId;

                  return (
                    <tr key={orden.id}>
                      <td>
                        <div className="proyecto-details">
                          <h5 className="proyecto-title">{orden.referencia}</h5>
                        </div>
                      </td>
                      <td>
                        <div className="avatar-group">
                          {uniqueAssigned.length > 0 ? (
                            <>
                              {uniqueAssigned.slice(0, 4).map((uid) => (
                                <Avatar
                                  key={uid}
                                  userId={creatorId}
                                  size={40}
                                  className="avatarOrden"
                                />
                              ))}
                              {assignedUsers.length > 4 && (
                                <span className="avatares-count">
                                  +{assignedUsers.length - 4}
                                </span>
                              )}
                            </>
                          ) : (
                            // si no hay asignados, muestro el avatar del usuario actual
                            <Avatar
                              userId={user?.uid}
                              size={32}
                              className="avatarOrden"
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="deadline-proyectos">
                          {calculateDaysLeft(orden.fechaFinRango)}
                        </span>
                      </td>
                      <td>
                        <span
                          className="metric-proyectos"
                          onClick={() => openModal(orden)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-chat"></i>{" "}
                          {orden.comentarios?.length || 0}
                        </span>

                        {modalData.isOpen &&
                          modalData.orden.id === orden.id && (
                            <div className="chat-modal-overlay">
                              <div className="chat-modal">
                                <div className="chat-modal-header">
                                  <h3>
                                    {`Comentarios para "${modalData.orden.referencia}"`}
                                  </h3>
                                  <button
                                    className="close-button"
                                    onClick={closeModal}
                                  >
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                </div>
                                <div className="chat-modal-body">
                                  {comentarios.length > 0 ? (
                                    comentarios.map((comentario, idx) => {
                                      const esComentarioMio =
                                        comentario.usuario?.id === usuario?.id;
                                      return (
                                        <div
                                          key={idx}
                                          className={`chat-message ${
                                            esComentarioMio
                                              ? "sent"
                                              : "received"
                                          }`}
                                        >
                                          {comentario.usuario &&
                                            comentario.usuario.nombre && (
                                              <p className="nombre-usuario-comentario">
                                                {comentario.usuario.nombre}{" "}
                                                {comentario.usuario.apellido}
                                              </p>
                                            )}
                                          <p>{comentario.texto}</p>
                                          <span className="message-time">
                                            {comentario.fecha}
                                          </span>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <p className="sin-comentarios">
                                      No hay comentarios todavía.
                                    </p>
                                  )}
                                </div>
                                <div className="chat-modal-footer">
                                  <input
                                    type="text"
                                    placeholder="Escribe un comentario..."
                                    value={nuevoComentario}
                                    onChange={(e) =>
                                      setNuevoComentario(e.target.value)
                                    }
                                    className="comentario-input"
                                  />
                                  <button
                                    onClick={() => agregarComentario(orden.id)}
                                    className="agregar-comentario"
                                  >
                                    Enviar
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                      </td>
                      <td>
                        <span
                          className={`status ${
                            orden.estado === "Pendiente"
                              ? "pendiente"
                              : orden.estado === "En Proceso"
                              ? "en-proceso"
                              : orden.estado === "Facturado"
                              ? "facturado"
                              : orden.estado === "Pagada"
                              ? "pagada"
                              : "default"
                          }`}
                        >
                          {orden.estado || "Sin estado"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PedidosActivos;
